import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config';

export function useProperties() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);

  const fetchProperties = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setError('');

    const token = localStorage.getItem('naijanest_token');

    try {
      const res = await fetch(`${API_BASE}/properties`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Failed to load properties: ${err.message}`);
      if (!background) {
        setProperties([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // The list is otherwise only ever fetched once at app startup and shared
  // across every page (browse, property details, favorites, ...) via
  // props - nothing refetches it, so a listing approved (or otherwise
  // changed) after that initial load never appears until a hard refresh.
  // Refetching on focus/visibility catches the common case of switching
  // back to an already-open tab after making a change elsewhere (e.g. the
  // admin panel in another tab).
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchProperties({ background: true });
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [fetchProperties]);

  const prependProperty = (property) => {
    setProperties((current) => [property, ...current]);
  };

  return {
    loading,
    error,
    properties,
    prependProperty,
    refetch: fetchProperties,
  };
}
